import styled from 'styled-components';

import { JsonObject, JsonValue } from '@components/json-editor';
import { ListCard } from '@components/list-card';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { capitalizeFirstLetter } from '@utils/strings';

import { ALL_VALUE_TYPES, ValueTypes, getIconBasedOnType, getType, getTypeName } from './utils';

export type JsonTypeTreeProps = {
  data: JsonObject;
  showIconForTypes?: ValueTypes[];
  unsupportedTypes?: ValueTypes[];
};

export const JsonTypeTree = ({
  data,
  showIconForTypes = [...ALL_VALUE_TYPES],
  unsupportedTypes = [],
}: JsonTypeTreeProps) => {
  return (
    <RootContainer>
      <ChildrenContainer $level={0} $drawLine={false}>
        {Object.entries(data).map(([key, value]) => (
          <JsonNode
            key={key}
            name={key}
            value={value}
            level={0}
            showIconForTypes={showIconForTypes}
            unsupportedTypes={unsupportedTypes}
          />
        ))}
      </ChildrenContainer>
    </RootContainer>
  );
};

type JsonNodeProps = {
  name: string;
  value: JsonValue;
  level: number;
  showIconForTypes: ValueTypes[];
  unsupportedTypes: ValueTypes[];
  hasUnsupportedParent?: boolean;
};

export const JsonNode = ({
  name,
  value,
  level,
  showIconForTypes,
  unsupportedTypes,
  hasUnsupportedParent = false,
}: JsonNodeProps) => {
  const { t } = useScopedTranslate(locale);
  const IconComponent = getIconBasedOnType(value, showIconForTypes);
  const type = getType(value);
  const isArray = type.startsWith('array');
  const isObject = type === 'object';

  const isUnsupportedNode = unsupportedTypes.includes(type);
  const title = isUnsupportedNode
    ? t('typeNotSupported', { name: name, type: getTypeName(type) })
    : name;

  const isUnsupported = hasUnsupportedParent || isUnsupportedNode;

  return (
    <>
      <Row $level={level}>
        {level === 0 ? (
          <Text font="semibold14" text={capitalizeFirstLetter(name)} />
        ) : (
          <TypeCard
            title={title}
            titleColor={isUnsupported ? 'elementRed100' : 'textPrimary'}
            icon={
              IconComponent ? (
                <IconComponent color={isUnsupported ? 'elementRed100' : 'elementPurple80'} />
              ) : undefined
            }
            titleFont="regular12"
          />
        )}
      </Row>

      {isObject && (
        <ChildrenContainer $level={level} $drawLine={level >= 1}>
          {Object.entries(value as { [k: string]: JsonValue }).map(
            ([childKey, childValue], idx, arr) => (
              <JsonNode
                key={childKey}
                name={childKey}
                value={childValue}
                level={level + 1}
                showIconForTypes={showIconForTypes}
                unsupportedTypes={unsupportedTypes}
                hasUnsupportedParent={isUnsupported}
              />
            )
          )}
        </ChildrenContainer>
      )}

      {isArray && (
        <ArrayChildren $level={level}>
          {(value as JsonValue[]).map((elem, idx) => {
            const arrayKey = `[${idx}]`;
            return (
              <JsonNode
                key={arrayKey}
                name={arrayKey}
                value={elem}
                level={level + 1}
                showIconForTypes={showIconForTypes}
                unsupportedTypes={unsupportedTypes}
                hasUnsupportedParent={isUnsupported}
              />
            );
          })}
        </ArrayChildren>
      )}
    </>
  );
};

const TypeCard = styled(ListCard)({
  height: pxToRem(40),
  flex: '1',
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
});

const RootContainer = styled(Stack)({
  background: theme.color.uiTrans5,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: `${pxToRem(12)} ${pxToRem(24)}`,

  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
});

const ChildrenContainer = styled.div<{ $level: number; $drawLine: boolean }>(
  ({ $level, $drawLine }) => {
    const lineLeftPx = $level * 32 - 16;

    return {
      marginTop: pxToRem(4),
      marginBottom: pxToRem(4),
      position: 'relative',

      '&::before': {
        content: $drawLine ? '""' : 'none',
        position: 'absolute',
        top: pxToRem(16),
        bottom: pxToRem(16),
        left: pxToRem(lineLeftPx),
        width: pxToRem(1),
        background: theme.color.uiTrans20,
      },
    };
  }
);

const ArrayChildren = styled.div<{ $level: number }>(({ $level }) => {
  const lineLeftPx = $level * 32 - 16;

  return {
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: pxToRem(16),
      bottom: pxToRem(16),
      left: pxToRem(lineLeftPx),
      width: pxToRem(1),
      background: theme.color.uiTrans20,
    },
  };
});

const Row = styled.div<{ $level: number }>(({ $level }) => {
  // calculate how many pixels we shift for this row's content
  const indentPx = $level >= 1 ? ($level - 1) * 32 : 0;

  return {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    padding: `${pxToRem(4)} 0`,
    paddingLeft: pxToRem(indentPx),
  };
});

const locale = {
  en: {
    typeNotSupported: '{name} • {type} not supported',
  },
};
